import { Button } from '@mantine/core';
import { BaseLayout, Icon, Link, toastNotifications, variants } from '@uag/react-core';
import {
    useGetCompanyGapiStatus,
    useRequestTemporaryAccessToDoka360Company,
} from '@uag/welcome-shared/src/api/queries/companies/companies';
import { CustomerNumberField } from '@uag/welcome-shared/src/components/customer/CustomerNumberField';
import { DOKA_360_PRODUCT_URL } from '@uag/welcome-shared/src/utils/externalLinks';
import { isAxiosError } from 'axios';
import { clsx } from 'clsx';
import { PropsWithChildren, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import secureLoginImage from './assets/secureLogin.svg';

const fieldMessageVariants = {
    information: {
        role: 'status',
        icon: 'info',
        iconClassName: 'text-icon-information',
        textClassName: 'text-text-information',
    },
    alert: { role: 'alert', icon: 'error', iconClassName: 'text-icon-alert', textClassName: 'text-text-alert' },
} as const;

const FieldMessage = ({ variant, children }: PropsWithChildren<{ variant: keyof typeof fieldMessageVariants }>) => {
    const { role, icon, iconClassName, textClassName } = fieldMessageVariants[variant];

    return (
        <div className="gap-sm flex w-full items-start" role={role}>
            <div className="flex h-5 items-center">
                <Icon className={iconClassName} icon={icon} />
            </div>
            <span className={clsx('leading-sm text-sm', textClassName)}>{children}</span>
        </div>
    );
};

export const TemporaryAccess = () => {
    const { t } = useTranslation();
    const [customerNumber, setCustomerNumber] = useState('');
    const [isCountrySupportedInDoka360, setIsCountrySupportedInDoka360] = useState(false);
    const { mutateAsync: requestTemporaryAccess, isPending } = useRequestTemporaryAccessToDoka360Company();
    const {
        data: gapiStatus,
        isPending: isGapiStatusPending,
        isFetching: isGapiStatusFetching,
        isError: isGapiStatusError,
        refetch: refetchGapiStatus,
    } = useGetCompanyGapiStatus(customerNumber, {
        query: { enabled: !!customerNumber && isCountrySupportedInDoka360 },
    });
    const isGapiStatusLoading = isGapiStatusPending || isGapiStatusFetching;
    const isGapiDisabled = !isGapiStatusError && gapiStatus?.data.isGapiEnabled === false;

    const handleRequestAccess = async () => {
        try {
            await requestTemporaryAccess({ customerIdentifier: customerNumber });
            // The backend enables GAPI as part of the access request.
            void refetchGapiStatus();
            toastNotifications.success({
                title: t('success'),
                message: t('requestTemporaryAccessSuccess'),
            });
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 403) {
                toastNotifications.error({
                    title: t('failed'),
                    message: t('requestTemporaryAccessForbidden'),
                });
                return;
            }

            const detail = isAxiosError(error) ? error.response?.data?.detail : undefined;
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                    message: detail ?? t('requestTemporaryAccessFailed'),
                },
                error,
            );
        }
    };

    return (
        <BaseLayout>
            <BaseLayout.Content>
                <div className="gap-2xl flex h-full flex-col items-center justify-center">
                    <div className="pr-6">
                        <img alt="" className="h-34" src={secureLoginImage} />
                    </div>
                    <div className="gap-lg flex w-124 flex-col items-center">
                        <div className="gap-lg flex w-full flex-col items-start">
                            <div className="flex w-full flex-col gap-1">
                                <h1 className="text-4xl font-bold">{t('requestTemporaryAccessTitle')}</h1>
                                <p>
                                    <Trans
                                        components={{
                                            doka360Link: (
                                                <Link
                                                    href={DOKA_360_PRODUCT_URL}
                                                    icon="open_in_new"
                                                    target="_blank"
                                                    variant={variants.link.external}
                                                />
                                            ),
                                        }}
                                        i18nKey="requestTemporaryAccessDescription"
                                    />
                                </p>
                            </div>
                            <div className="gap-sm flex w-full flex-col">
                                <CustomerNumberField
                                    initialCustomerNumber={customerNumber}
                                    showDescription={false}
                                    fullWidth
                                    requireDoka360Support
                                    onCustomerNumberChanged={(value, isSupported) => {
                                        setCustomerNumber(value);
                                        setIsCountrySupportedInDoka360(isSupported);
                                    }}
                                />
                                {isGapiDisabled && (
                                    <FieldMessage variant="information">
                                        {t('requestTemporaryAccessGapiDisabled')}
                                    </FieldMessage>
                                )}
                                {isGapiStatusError && (
                                    <FieldMessage variant="alert">{t('gapiStatusError')}</FieldMessage>
                                )}
                            </div>
                        </div>
                        <Button
                            disabled={!customerNumber || !isCountrySupportedInDoka360 || isGapiStatusLoading}
                            loading={isPending}
                            fullWidth
                            onClick={handleRequestAccess}
                        >
                            {t('requestAccess')}
                        </Button>
                    </div>
                </div>
            </BaseLayout.Content>
        </BaseLayout>
    );
};
